import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { Group, Stack, Tabs } from '@ekhodealer/ekho-common/rev';

import type { TabItem } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { StatBox } from '@common/components/StatBox';
import { formatCurrency } from '@common/lib/currency';

import { useGetOrderPaymentFai } from '../../api/useGetOrderPaymentFai';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';
import { SectionSkeleton } from '../order-details/shared/SectionSkeleton';

import { Receipt } from './Receipt';
import { TransactionList } from './TransactionList';

const RECEIPT_TAB = 'receipt';
const TRANSACTIONS_TAB = 'transactions';
const SECTION_TITLE = 'Buyer payments';

export const BuyerPaymentsSection = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderPaymentFai, isLoading } = useGetOrderPaymentFai(
		selectedRow?.purchasedVehicleId || undefined,
	);
	const [selectedTab, setSelectedTab] = useState<string>(RECEIPT_TAB);

	if (isLoading) {
		return <SectionSkeleton title={SECTION_TITLE} />;
	}

	if (!orderPaymentFai) {
		return null;
	}

	const { buyerPayments } = orderPaymentFai;
	const { paid, balance, receipt, transactions } = buyerPayments;

	const tabItems: TabItem[] = [
		{ id: RECEIPT_TAB, content: 'Receipt' },
		{ id: TRANSACTIONS_TAB, content: 'Transactions' },
	];

	const handleTabSelect = (item: TabItem) => {
		setSelectedTab(item.id);
	};

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<Stack itemsSpacing='16'>
				<Group itemsSpacing='16' noWrap itemsAlignX='stretch'>
					<StatBox title='Paid' detail={formatCurrency(paid)} />
					<StatBox title='Balance' detail={formatCurrency(balance)} />
				</Group>

				<Tabs items={tabItems} selectedKey={selectedTab} onSelect={handleTabSelect} />

				{selectedTab === RECEIPT_TAB && receipt && <Receipt receipt={receipt} />}
				{selectedTab === TRANSACTIONS_TAB && (
					<TransactionList
						transactions={transactions}
						totalLabel='Total transactions'
						emptyMessage='No transactions for this order'
					/>
				)}
			</Stack>
		</AccordionSection>
	);
};
